import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useShuddhoshikhaContext } from './useShuddhoshikhaContext';
import { currentUser } from '../data/userData';
import { Bell, FileText, Calendar, ShieldAlert, Award } from 'lucide-react';

export interface RealTimeMessage {
  id: number;
  title: string;
  description: string;
  type: 'notification' | 'notice';
  time: string;
  date: string;
  unread: boolean;
  icon?: any;
  iconColor?: string;
}

// Map of standard icon types to Lucide Icons
const iconMap: Record<string, any> = {
  Award,
  Bell,
  FileText,
  ShieldAlert,
  Calendar
};

// Initial/default mock notifications matching layout
const initialNotifications: RealTimeMessage[] = [
  {
    id: 1,
    title: 'গণিত কুইজের ফলাফল',
    description: 'অধ্যায় ২ এর অনুশীলন কুইজে আপনি ৮০% নম্বর পেয়ে উত্তীর্ণ হয়েছেন!',
    time: '১০ মিনিট আগে',
    date: '১২ জুন, ২০২৬',
    type: 'notification',
    iconColor: 'text-amber-500 bg-amber-500/10',
    icon: Award,
    unread: true,
  },
  {
    id: 2,
    title: 'নতুন কোর্স যুক্ত হয়েছে',
    description: 'রসায়ন ১ম পত্রের "জৈব রসায়ন" অধ্যায়ের ওপর নতুন ১০টি লেকচার যোগ করা হয়েছে।',
    time: '২ ঘণ্টা আগে',
    date: '১২ জুন, ২০২৬',
    type: 'notification',
    iconColor: 'text-blue-500 bg-blue-500/10',
    icon: Bell,
    unread: true,
  },
  {
    id: 3,
    title: 'মক টেস্ট চ্যালেঞ্জ',
    description: 'রাহুল আহমেদ আপনাকে "পদার্থবিজ্ঞান চলতড়িৎ" এর একটি চ্যালেঞ্জ মক টেস্টে আমন্ত্রণ জানিয়েছেন।',
    time: '১ দিন আগে',
    date: '১১ জুন, ২০২৬',
    type: 'notification',
    iconColor: 'text-purple-500 bg-purple-500/10',
    icon: Bell,
    unread: false,
  },
];

// Initial/default mock notices
const initialNotices: RealTimeMessage[] = [
  {
    id: 101,
    title: 'বোর্ড পরীক্ষা ২০২৭ সিলেবাস',
    description: 'শিক্ষা বোর্ড কর্তৃক প্রকাশিত এসএসসি ২০২৭ পরীক্ষার সংশোধিত সংক্ষিপ্ত সিলেবাস সংগ্রহ করুন।',
    time: '৫ দিন আগে',
    date: '০৭ জুন, ২০২৬',
    type: 'notice',
    iconColor: 'text-emerald-500 bg-emerald-500/10',
    icon: FileText,
    unread: false,
  },
  {
    id: 102,
    title: 'সার্ভার রক্ষণাবেক্ষণ নোটিশ',
    description: 'সিস্টেম আপগ্রেডের জন্য আজ রাত ১২:০০ টা থেকে রাত ৩:০০ টা পর্যন্ত ওয়েবসাইট রক্ষণাবেক্ষণ কাজ চলবে।',
    time: '৬ দিন আগে',
    date: '০৬ জুন, ২০২৬',
    type: 'notice',
    iconColor: 'text-rose-500 bg-rose-500/10',
    icon: ShieldAlert,
    unread: false,
  },
  {
    id: 103,
    title: 'কুইজ প্রতিযোগিতা সিজন ২',
    description: 'আগামী সপ্তাহ থেকে শুরু হতে যাচ্ছে শুদ্ধশিক্ষা লাইভ কুইজ প্রতিযোগিতা। আকর্ষণীয় সব উপহার লাইভ লুফে নিন!',
    time: '১১ দিন আগে',
    date: '০১ জুন, ২০২৬',
    type: 'notice',
    iconColor: 'text-cyan-500 bg-cyan-500/10',
    icon: Calendar,
    unread: false,
  },
];

export function useRealTimeMessages() {
  const { profile } = useShuddhoshikhaContext();
  const [notifications, setNotifications] = useState<RealTimeMessage[]>(initialNotifications);
  const [notices, setNotices] = useState<RealTimeMessage[]>(initialNotices);
  const [unreadCount, setUnreadCount] = useState<number>(2); // 2 unread mock notifications initially
  const socketRef = useRef<Socket | null>(null);

  // Sync state values on load (in case there are pre-stored messages in local storage, etc.)
  useEffect(() => {
    const totalUnread = 
      notifications.filter(n => n.unread).length + 
      notices.filter(n => n.unread).length;
    setUnreadCount(totalUnread);
  }, [notifications, notices]);

  useEffect(() => {
    // Determine the socket endpoint (Default is localhost, fallback to window configuration)
    const socketUrl = (window as any).shSocketUrl || (window as any).shAuth?.shSocketUrl || 'http://localhost:3001';

    // 1. Establish connection with auth query metadata
    const userMeta = {
      userId: currentUser.id || 'guest',
      class: profile?.target_batch || currentUser.class || 'ssc_2027',
      group: profile?.sh_group || currentUser.group || 'science'
    };

    console.log('[Socket] Connecting with student metadata:', userMeta);

    const socket = io(socketUrl, {
      query: {
        userId: userMeta.userId,
        class: userMeta.class,
        group: userMeta.group
      },
      transports: ['websocket'],
      reconnectionAttempts: 5,
      reconnectionDelay: 2000
    });

    socketRef.current = socket;

    // 2. Connection success handler
    socket.on('connect', () => {
      console.log('[Socket] Connected successfully, socket ID:', socket.id);
      
      // Secondary explicit backup join request
      socket.emit('join_rooms', {
        userId: userMeta.userId,
        classLevel: userMeta.class,
        groupName: userMeta.group
      });
    });

    // 3. Listen for live incoming broadcast messages
    socket.on('new_message', (data: any) => {
      console.log('[Socket] New real-time message received:', data);

      // Resolve icon dynamically if icon name string is passed
      let resolvedIcon = Bell;
      if (data.icon && iconMap[data.icon]) {
        resolvedIcon = iconMap[data.icon];
      } else if (data.type === 'notice') {
        resolvedIcon = FileText;
      }

      const formattedMessage: RealTimeMessage = {
        id: data.id || Date.now(),
        title: data.title || 'নতুন আপডেট',
        description: data.description || data.text || '',
        type: data.type === 'notice' ? 'notice' : 'notification',
        time: data.time || 'এইমাত্র',
        date: data.date || new Date().toLocaleDateString('bn-BD'),
        unread: true,
        icon: resolvedIcon,
        iconColor: data.iconColor || (data.type === 'notice' ? 'text-emerald-500 bg-emerald-500/10' : 'text-blue-500 bg-blue-500/10')
      };

      // Play a subtle notification sound effect
      try {
        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2568/2568-84.wav');
        audio.volume = 0.5;
        audio.play().catch(() => {}); // catch autoplay policy blocks
      } catch (e) {
        // ignore audio play errors
      }

      // Add to corresponding list state
      if (formattedMessage.type === 'notice') {
        setNotices(prev => [formattedMessage, ...prev]);
      } else {
        setNotifications(prev => [formattedMessage, ...prev]);
      }
    });

    // Handle Connection Error
    socket.on('connect_error', (error) => {
      console.warn('[Socket] Connection failed or server offline:', error.message);
    });

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [profile]);

  /**
   * Optimistically marks a notification/notice as read
   */
  const markAsRead = async (messageId: number) => {
    // 1. Optimistic State Updates
    let found = false;

    setNotifications(prev => 
      prev.map(item => {
        if (item.id === messageId && item.unread) {
          found = true;
          return { ...item, unread: false };
        }
        return item;
      })
    );

    setNotices(prev => 
      prev.map(item => {
        if (item.id === messageId && item.unread) {
          found = true;
          return { ...item, unread: false };
        }
        return item;
      })
    );

    if (!found) return;

    const shAuth = (window as any).shAuth;
    const nonce = shAuth?.rest_nonce || shAuth?.nonce || '';
    
    try {
      await fetch('/wp-json/shuddhoshikha/v1/notifications/read', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce
        },
        body: JSON.stringify({ notification_id: messageId.toString() })
      });
      console.log(`[+] Persisted read status for notification: ${messageId}`);
    } catch (err) {
      // Offline fallback: keep optimistic change but log issue
      console.warn('[-] Failed to persist read state to backend database:', err);
    }
  };

  return {
    notifications,
    notices,
    unreadCount,
    markAsRead
  };
}
